// archiveHandlers/aksioma01Handler.js
import axios from 'axios';
import {
    API_URL, formatDateTimeWith24, formatValue,
    getAdjustedDateFrom, getAdjustedDateTo, getDatePlusOne, switchConvert
} from '../archiveUtils';

// Для Аксиома-01 НС = -1
const NS_VALUE_A01 = -1;

// ============================================
// Суффиксы имён параметров по типу архива
// hour → без суффикса, day → "с", month → "м", integral → без суффикса
// ============================================
const SUFFIX = {
    hour: 'ч',
    day: 'с',
    month: 'м',
    integral: '',
};

// ============================================
// Форматирование даты из полей date + time
// Логика как в Аксиома-03: "25.07.2026 00:00:00" → "24.07.2026 24"
// ============================================
const formatDateTimeA01 = (record) => {
    if (!record) return '—';
    const date = record.date || '';
    const time = record.time || '';
    if (!date) return '—';

    // Парсим дату — может быть "DD.MM.YYYY" или "YYYY-MM-DD"
    let d;
    if (date.includes('.')) {
        // Формат DD.MM.YYYY
        const parts = date.split('.');
        const timeParts = time ? time.split(':') : ['0', '0', '0'];
        d = new Date(
            parseInt(parts[2]),           // year
            parseInt(parts[1]) - 1,       // month (0-based)
            parseInt(parts[0]),           // day
            parseInt(timeParts[0] || 0),  // hours
            parseInt(timeParts[1] || 0),  // minutes
            parseInt(timeParts[2] || 0)   // seconds
        );
    } else {
        // Формат YYYY-MM-DD
        d = new Date(`${date}T${time || '00:00:00'}`);
    }

    if (isNaN(d.getTime())) return `${date} ${time}`.trim() || '—';

    let hours = d.getHours();
    let displayDate = new Date(d);

    // Если час = 00 → показываем предыдущий день с часом 24
    if (hours === 0) {
        displayDate.setDate(displayDate.getDate() - 1);
        hours = 24;
    }

    const day = String(displayDate.getDate()).padStart(2, '0');
    const month = String(displayDate.getMonth() + 1).padStart(2, '0');
    const year = displayDate.getFullYear();
    const hoursStr = String(hours).padStart(2, '0');
    return `${day}.${month}.${year} ${hoursStr}`;
};

// ============================================
// Построение колонок для часового/суточного/месячного архива
// ============================================
const buildArchiveColumns = (sampleRecord, archiveType) => {
    if (!sampleRecord) return [];

    const suffix = SUFFIX[archiveType] || '';
    const columns = [];
    columns.push({ key: 'datetime', name: 'Дата/Время', type: 'datetime' });

    // Время работы / простоя (без суффикса)
    if (switchConvert(0, sampleRecord.switch_time)) {
        columns.push({ key: 'time_work', name: 'Наработка (ВНo), час', unit: 'час', type: 'time', source: 'time_work' });
    }
    if (switchConvert(1, sampleRecord.switch_time)) {
        columns.push({ key: 'energi_time_work_1', name: 'Наработка (ВНg), час', unit: 'час', type: 'time', source: 'energi_time_work_1' });
    }
    if (switchConvert(0, sampleRecord.switch_time)) {
        columns.push({ key: 'time_NOT_work', name: 'Простой (ВПo), час', unit: 'час', type: 'time', source: 'time_NOT_work' });
    }
    if (switchConvert(1, sampleRecord.switch_time)) {
        columns.push({ key: 'energi_time_NOT_work_1', name: 'Простой (ВПg), час', unit: 'час', type: 'time', source: 'energi_time_NOT_work_1' });
    }

    // Температура
    const tempParams = [
        { key: 't1', name: 't1' }, { key: 't2', name: 't2' }, { key: 't3', name: 't3' }, { key: 't4', name: 't4' },
        { key: 'tx', name: 'tx' }, { key: 'dto', name: 'dto' }, { key: 'dtg', name: 'dtg' }, { key: 'tok', name: 'tok' },
    ];
    tempParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_temperatura)) {
            columns.push({ key: p.key, name: `${p.name}${suffix}, °C`, unit: '°C', type: 'value', category: 'temperatura' });
        }
    });

    // Давление
    const pressureParams = [
        { key: 'P1', name: 'P1' }, { key: 'P2', name: 'P2' }, { key: 'P3', name: 'P3' }, { key: 'P4', name: 'P4' },
        { key: 'Px', name: 'Px' }, { key: 'dPo', name: 'dPo' }, { key: 'dPg', name: 'dPg' },
    ];
    pressureParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_davlenie)) {
            columns.push({ key: p.key, name: `${p.name}${suffix}, МПа`, unit: 'МПа', type: 'value', category: 'davlenie' });
        }
    });

    // Объёмный расход
    const volumeParams = [
        { key: 'Go1', name: 'Go1' }, { key: 'Go2', name: 'Go2' }, { key: 'Go3', name: 'Go3' }, { key: 'Go4', name: 'Go4' },
        { key: 'Gox', name: 'Gox' }, { key: 'Goo', name: 'Goo' }, { key: 'Gog', name: 'Gog' },
    ];
    volumeParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_rasxod)) {
            columns.push({ key: p.key, name: `${p.name}${suffix}, м³`, unit: 'м³', type: 'value', category: 'obemnui_rasxod' });
        }
    });

    // Массовый расход
    const massParams = [
        { key: 'Gm1', name: 'Gm1' }, { key: 'Gm2', name: 'Gm2' }, { key: 'Gm3', name: 'Gm3' }, { key: 'Gm4', name: 'Gm4' },
        { key: 'Gmx', name: 'Gmx' }, { key: 'Gmo', name: 'Gmo' }, { key: 'Gmg', name: 'Gmg' },
    ];
    massParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_rasxod_massa)) {
            columns.push({ key: p.key, name: `${p.name}${suffix}, т`, unit: 'т', type: 'value', category: 'massovui_rasxod' });
        }
    });

    // Энергия
    const energyParams = [{ key: 'Qro', name: 'Qro' }, { key: 'Qrg', name: 'Qrg' }];
    energyParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_energi)) {
            columns.push({ key: p.key, name: `${p.name}${suffix}, Гкал`, unit: 'Гкал', type: 'value', category: 'energi' });
        }
    });

    return columns;
};

// ============================================
// Построение колонок для интегрального архива
// ============================================
const buildIntegralColumns = (sampleRecord) => {
    if (!sampleRecord) return [];

    const columns = [];
    columns.push({ key: 'datetime', name: 'Дата/Время', type: 'datetime' });

    // Время (без суффикса)
    if (switchConvert(0, sampleRecord.switch_time)) {
        columns.push({ key: 'time_work', name: 'Наработка (ВНo), час', unit: 'час', type: 'time', source: 'time_work' });
    }
    if (switchConvert(1, sampleRecord.switch_time)) {
        columns.push({ key: 'energi_time_work_in_min_1', name: 'Наработка (ВНg), час', unit: 'час', type: 'time', source: 'energi_time_work_in_min_1' });
    }
    if (switchConvert(0, sampleRecord.switch_time)) {
        columns.push({ key: 'time_NOT_work', name: 'Простой (ВПo), час', unit: 'час', type: 'time', source: 'time_NOT_work' });
    }
    if (switchConvert(1, sampleRecord.switch_time)) {
        columns.push({ key: 'energi_time_NOT_work_in_min_1', name: 'Простой (ВПg), час', unit: 'час', type: 'time', source: 'energi_time_NOT_work_in_min_1' });
    }

    // Объём (накопленный) — без суффикса
    const volumeParams = [
        { key: 'V1', name: 'V1' }, { key: 'V2', name: 'V2' }, { key: 'V3', name: 'V3' }, { key: 'V4', name: 'V4' },
        { key: 'Vx', name: 'Vx' }, { key: 'Vo', name: 'Vo' }, { key: 'Vg', name: 'Vg' },
    ];
    volumeParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_rasxod)) {
            columns.push({ key: p.key, name: `${p.name}, м³`, unit: 'м³', type: 'value', category: 'obemnui_rasxod' });
        }
    });

    // Масса (накопленная) — без суффикса
    const massParams = [
        { key: 'M1', name: 'M1' }, { key: 'M2', name: 'M2' }, { key: 'M3', name: 'M3' }, { key: 'M4', name: 'M4' },
        { key: 'Mx', name: 'Mx' }, { key: 'Mo', name: 'Mo' }, { key: 'Mg', name: 'Mg' },
    ];
    massParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_rasxod_massa)) {
            columns.push({ key: p.key, name: `${p.name}, т`, unit: 'т', type: 'value', category: 'massovui_rasxod' });
        }
    });

    // Энергия (накопленная) — без суффикса
    const energyParams = [{ key: 'Qo', name: 'Qo' }, { key: 'Qg', name: 'Qg' }];
    energyParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_energi)) {
            columns.push({ key: p.key, name: `${p.name}, Гкал`, unit: 'Гкал', type: 'value', category: 'energi' });
        }
    });

    return columns;
};

// ============================================
// Получение значения ячейки
// ============================================
const getCellValueA01 = (record, col) => {
    if (col.type === 'datetime') {
        return formatDateTimeA01(record);
    }
    if (col.type === 'time') {
        const raw = record[col.source];
        if (raw === null || raw === undefined) return null;
        const val = parseFloat(raw) / 60; // минуты → часы
        return isNaN(val) ? null : val;
    }
    // type === 'value'
    const val = record[col.key];
    if (val === null || val === undefined) return null;
    const num = parseFloat(val);
    if (isNaN(num)) return null;
    if (num === NS_VALUE_A01) return NS_VALUE_A01;
    return num;
};

// ============================================
// Маппинг: ключ расходного архива → ключ интегрального
// ============================================
const FLOW_TO_ACCUM_MAP = {
    'Go1': 'V1', 'Go2': 'V2', 'Go3': 'V3', 'Go4': 'V4',
    'Gox': 'Vx', 'Goo': 'Vo', 'Gog': 'Vg',
    'Gm1': 'M1', 'Gm2': 'M2', 'Gm3': 'M3', 'Gm4': 'M4',
    'Gmx': 'Mx', 'Gmo': 'Mo', 'Gmg': 'Mg',
    'Qro': 'Qo', 'Qrg': 'Qg',
};

// ============================================
// Получение накопленного значения из интегральной записи
// ============================================
const getAccumulatedValueA01 = (accumData, colKey, _podSystemIndex) => {
    if (!accumData) return null;

    // Время
    if (colKey === 'time_work') {
        const val = accumData.time_work;
        return val !== undefined && val !== null ? parseFloat(val) / 60 : null;
    }
    if (colKey === 'time_NOT_work') {
        const val = accumData.time_NOT_work;
        return val !== undefined && val !== null ? parseFloat(val) / 60 : null;
    }
    if (colKey === 'energi_time_work_1' || colKey === 'energi_time_work_in_min_1') {
        const val = accumData.energi_time_work_in_min_1 ?? accumData.energi_time_work_1;
        return val !== undefined && val !== null ? parseFloat(val) / 60 : null;
    }
    if (colKey === 'energi_time_NOT_work_1' || colKey === 'energi_time_NOT_work_in_min_1') {
        const val = accumData.energi_time_NOT_work_in_min_1 ?? accumData.energi_time_NOT_work_1;
        return val !== undefined && val !== null ? parseFloat(val) / 60 : null;
    }

    // Если ключ уже интегральный (V1, M1, Qo...) — берём напрямую
    if (accumData[colKey] !== undefined) {
        const num = parseFloat(accumData[colKey]);
        return isNaN(num) ? null : num;
    }

    // Если ключ расходный (Go1, Gm1, Qro...) — маппим в интегральный
    const accumKey = FLOW_TO_ACCUM_MAP[colKey];
    if (accumKey && accumData[accumKey] !== undefined) {
        const num = parseFloat(accumData[accumKey]);
        return isNaN(num) ? null : num;
    }

    return null;
};

// ============================================
// Построение колонок для второй (накопленной) таблицы
// Без температуры и давления, но С датой
// ============================================
const buildAccumColumnsA01 = (columns) => {
    return columns
        .filter(col => {
            // Убираем только температуру и давление
            if (col.category === 'temperatura' || col.category === 'davlenie') return false;
            return true;
        })
        .map(col => {
            const accumKey = FLOW_TO_ACCUM_MAP[col.key];
            if (accumKey) {
                return { ...col, key: accumKey, name: `${accumKey}, ${col.unit}` };
            }
            return col;
        });
};

// ============================================
// Форматирование значения для отображения
// ============================================
const formatValueA01 = (val, decimals = 3) => {
    if (val === null || val === undefined || val === '') return '—';
    const num = parseFloat(val);
    if (isNaN(num)) return '—';
    if (num === NS_VALUE_A01) return 'НС';
    return num.toLocaleString('ru-RU', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

// ============================================
// Основной экспорт: генерация отчетов для Аксиома-01
// ============================================
export const generateAksioma01Reports = async (devName, params, headers, deviceFromList) => {
    const reports = [];

    let deviceInfo = {};
    try {
        const infoRes = await axios.get(`${API_URL}/api/devices/aksioma01/${devName}/info`, { headers });
        deviceInfo = infoRes.data;
    } catch (e) {
        deviceInfo = { name: devName, serial_number: '—' };
    }

    const adjustedDateFrom = getAdjustedDateFrom(params.dateFrom, params.archiveType);
    const adjustedDateTo = getAdjustedDateTo(params.dateTo, params.archiveType);

    const archiveRes = await axios.get(`${API_URL}/api/devices/aksioma01/${devName}/archive`, {
        params: { type: params.archiveType, from: adjustedDateFrom, to: adjustedDateTo }, headers
    });

    const archiveData = archiveRes.data;
    const records = archiveData.records || [];

    if (records.length === 0) return reports;

    const sampleRecord = records[0];
    let columns;
    if (params.archiveType === 'integral') {
        columns = buildIntegralColumns(sampleRecord);
    } else {
        columns = buildArchiveColumns(sampleRecord, params.archiveType);
    }

    const sortedRecords = [...records].sort((a, b) => {
        const dateA = new Date(`${a.date || ''} ${a.time || ''}`).getTime() || 0;
        const dateB = new Date(`${b.date || ''} ${b.time || ''}`).getTime() || 0;
        return dateA - dateB;
    });

    const processedRecords = sortedRecords.map(rec => {
        const row = {};
        columns.forEach(col => {
            row[col.key] = getCellValueA01(rec, col);
        });
        return row;
    });

    const totals = {};
    const sums = {};
    const counts = {};

    processedRecords.forEach(r => {
        columns.forEach(col => {
            if (col.type === 'datetime') return;
            const val = r[col.key];
            if (val === NS_VALUE_A01 || val == null || isNaN(parseFloat(val))) return;
            const num = parseFloat(val);
            if (!totals[col.key]) totals[col.key] = 0;
            if (!sums[col.key]) sums[col.key] = 0;
            if (!counts[col.key]) counts[col.key] = 0;

            if (col.category === 'temperatura' || col.category === 'davlenie') {
                sums[col.key] += num;
                counts[col.key]++;
            } else {
                totals[col.key] += num;
            }
        });
    });

    const averages = {};
    Object.keys(sums).forEach(key => {
        averages[key] = counts[key] > 0 ? sums[key] / counts[key] : NS_VALUE_A01;
    });

    let startAccum = null, endAccum = null;
    if (params.archiveType !== 'integral') {
        try {
            const startRes = await axios.get(`${API_URL}/api/devices/aksioma01/${devName}/archive`, {
                params: { type: 'integral', from: params.dateFrom, to: params.dateFrom }, headers
            });
            startAccum = startRes.data?.records?.[0] || null;
        } catch (e) { }

        const dateToPlusOne = getDatePlusOne(params.dateTo).toISOString().split('T')[0];
        try {
            const endRes = await axios.get(`${API_URL}/api/devices/aksioma01/${devName}/archive`, {
                params: { type: 'integral', from: dateToPlusOne, to: dateToPlusOne }, headers
            });
            endAccum = endRes.data?.records?.[0] || null;
        } catch (e) { }
    }

    reports.push({
        deviceInfo,
        deviceFromList,
        deviceName: devName,
        deviceType: 'aksioma01',
        podSystemName: null, // ← Аксиома-01 не имеет подсистем
        podSystemIndex: 0,
        columns,
        records: processedRecords,
        totals,
        averages,
        startAccumulated: startAccum,
        endAccumulated: endAccum,
        archiveType: params.archiveType
    });

    return reports;
};

export {
    buildAccumColumnsA01,
    getAccumulatedValueA01,
    getCellValueA01,
    formatValueA01,
    NS_VALUE_A01
};